
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  ClipboardCheck, 
  FileText, 
  Save, 
  Copy, 
  Download, 
  X, 
  Check,
  Sparkles,
  BarChart 
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SessionSummaryProps {
  sessionId: string;
  sessionName: string;
  sessionDuration: number;
  participantCount: number;
  isGenerating?: boolean;
  onGenerateSummary?: () => void;
}

const SessionSummary: React.FC<SessionSummaryProps> = ({
  sessionId,
  sessionName,
  sessionDuration,
  participantCount,
  isGenerating = false,
  onGenerateSummary
}) => {
  const { toast } = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [summaryText, setSummaryText] = useState(
    "This session focused on recording vocals for the 'Summer Nights' track. " +
    "Alex performed 3 vocal takes with Sam providing guitar accompaniment. " +
    "The team agreed that the second take was the strongest, with Taylor suggesting a double-tracking approach for the chorus. " +
    "Jamie will mix the vocals this week, and the team will reconvene on Friday to review.\n\n" +
    "Action Items:\n" +
    "- Jamie: Complete vocal mix by Thursday\n" +
    "- Sam: Send additional guitar parts by Wednesday\n" +
    "- Alex: Schedule time for chorus double-tracking\n" +
    "- Team: Next review session Friday @ 2pm"
  );
  
  const [editableText, setEditableText] = useState(summaryText);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(summaryText);
    setIsCopied(true);
    
    toast({
      title: "Copied to clipboard",
      description: "Session summary has been copied to your clipboard."
    });
    
    setTimeout(() => setIsCopied(false), 2000);
  };
  
  const handleDownload = () => {
    const blob = new Blob([summaryText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sessionName.replace(/\s+/g, '_')}_Summary.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Summary Downloaded",
      description: "Session summary has been downloaded as a text file."
    });
  };
  
  const handleSaveEdit = () => {
    setSummaryText(editableText);
    setIsEditing(false);
    
    toast({
      title: "Summary Updated",
      description: "Your changes to the session summary have been saved."
    });
  };
  
  const handleCancelEdit = () => {
    setEditableText(summaryText);
    setIsEditing(false);
  };
  
  const handleGenerateSummary = () => {
    if (onGenerateSummary) {
      onGenerateSummary();
    } else {
      toast({
        title: "Generating Summary",
        description: "AI is analyzing your session and will generate a summary shortly."
      });
      
      // Simulate AI generation
      setTimeout(() => {
        setSummaryText(
          "This vocal recording session for 'Summer Nights' included 6 participants and lasted 1 hour 20 minutes. " +
          "Alex completed 3 full vocal takes, with Take 2 identified as the strongest by the group. " +
          "Sam provided guitar backing, with some timing issues noted at the bridge section (1:45-2:10). " +
          "Taylor suggested double-tracking the chorus for added depth, which was tested in the final 20 minutes. " +
          "The session used a Neumann U87 microphone with noise reduction set to 65%.\n\n" +
          "Key Decisions:\n" +
          "- Use Take 2 as the primary vocal\n" +
          "- Double-track chorus sections\n" +
          "- Re-record guitar bridge section\n\n" +
          "Action Items:\n" +
          "- Jamie: Mix vocals by Thursday\n" +
          "- Sam: Re-record bridge guitar part\n" +
          "- Alex: Schedule time for chorus double-tracking\n" +
          "- All: Review session Friday at 2pm"
        );
        
        toast({
          title: "Summary Generated",
          description: "AI has analyzed your session and generated a detailed summary."
        });
      }, 3000);
    }
  };
  
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-purple-400" />
          <h3 className="text-sm font-medium text-white">Session Summary</h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {participantCount} participants
          </Badge>
          <Badge variant="outline" className="text-xs">
            {Math.floor(sessionDuration / 60)}h {sessionDuration % 60}m
          </Badge>
        </div>
      </div>
      
      {isEditing ? (
        <div className="flex-1 p-3">
          <Textarea
            value={editableText}
            onChange={(e) => setEditableText(e.target.value)}
            className="min-h-[200px] bg-white/5 border-white/10 text-white resize-none h-full"
          />
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              variant="outline" 
              className="gap-1"
              onClick={handleSaveEdit}
            >
              <Save className="h-3 w-3" />
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost" 
              className="gap-1"
              onClick={handleCancelEdit}
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ScrollArea className="flex-1 p-3">
            <div className="whitespace-pre-wrap text-sm text-white/90">
              {summaryText || (
                <div className="text-white/50 italic text-center py-12">
                  {isGenerating ? 
                    "AI is analyzing your session and generating a summary..." :
                    "No summary available. Generate one with AI or create manually."
                  }
                </div>
              )}
            </div>
          </ScrollArea>
          
          <div className="p-3 border-t border-white/10 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={summaryText ? "outline" : "default"}
              className="gap-1"
              onClick={() => setIsEditing(true)}
              disabled={isGenerating}
            >
              <ClipboardCheck className="h-3 w-3" />
              Edit
            </Button>
            
            <Button
              size="sm"
              variant={summaryText ? "outline" : "default"} 
              className="gap-1"
              onClick={handleCopy}
              disabled={!summaryText || isGenerating}
            >
              {isCopied ? (
                <>
                  <Check className="h-3 w-3" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  Copy
                </>
              )}
            </Button>
            
            <Button
              size="sm"
              variant={summaryText ? "outline" : "default"} 
              className="gap-1"
              onClick={handleDownload}
              disabled={!summaryText || isGenerating}
            >
              <Download className="h-3 w-3" />
              Download
            </Button>
            
            <Button
              size="sm"
              variant="neon" 
              className="gap-1 ml-auto"
              onClick={handleGenerateSummary}
              disabled={isGenerating}
            >
              <Sparkles className="h-3 w-3" />
              {summaryText ? "Regenerate" : "Generate"} with AI
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default SessionSummary;
